import * as os from "node:os";
import * as path from "node:path";
import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import { readCompanionRelease } from "../../lib/companion-channel.js";
import { platformTag } from "@m8t-stack/platform-release";
import { CHANNEL_LATEST_URL } from "../../lib/release-channel.js";
import { downloadCompanionArtifact } from "../../lib/companion-download.js";
import {
  getCompanionStartAtLogin,
  installCompanion,
  setCompanionStartAtLogin,
  type CompanionHostOptions,
  type CompanionInstallOptions,
  type CompanionInstallState,
} from "../../lib/companion-install.js";

interface WritableLike {
  write(chunk: string): unknown;
}

/**
 * What is true of this machine. Reading and removing an install need only
 * this — no gateway, no network, and no build.
 */
export function defaultLocalCompanionInstallOptions(): CompanionHostOptions {
  const cliEntry = process.argv[1];
  if (!cliEntry) throw new Error("Cannot resolve the installed CLI entry");
  const homeDirectory = os.homedir();
  const platform = process.platform;
  return {
    homeDirectory,
    platform,
    architecture: process.arch,
    localAppData: process.env.LOCALAPPDATA,
    nodeExecutable: process.execPath,
    cliEntry,
    setStartAtLogin: (executable, enabled) =>
      setCompanionStartAtLogin({ homeDirectory, platform, executable, enabled }),
    getStartAtLogin: (executable) =>
      getCompanionStartAtLogin({ homeDirectory, platform, executable }),
  };
}

/**
 * A channel that carries no companions is an ordinary answer, not a failure:
 * every release cut before they existed has none, so callers report it rather
 * than offering to repair something unpublished. They do REPORT it — onboarding
 * used to pass over it in silence, which is where a genuine failure hid.
 */
export type CompanionConvergeOutcome =
  | CompanionInstallState
  | { readonly state: "not-released" };

export interface CompanionConvergeOptions {
  /**
   * A build staged locally instead of one from the channel — how a contributor
   * installs what they just compiled. Everything after this point is identical,
   * verification included.
   */
  readonly from?: string;
  /**
   * Which deployment to bind to, when a subscription holds more than one and
   * discovery cannot choose. Every other command that resolves a gateway takes
   * this, and the error discovery raises names it.
   */
  readonly resourceGroup?: string;
  /**
   * The platform release to take the companion FROM, e.g. `platform-v0.6.3`.
   *
   * Without it the channel's `latest` answers, which is the wrong question
   * during a from-zero install of a specific pinned platform: what matters is
   * what the release being installed carries, not what is newest. The install
   * path knows its version (the status doc names it) and passes it; `m8t
   * companion install`, typed by hand against whatever is deployed, does not —
   * and its copy says `latest` rather than claiming a pin it never read.
   */
  readonly platformVersion?: string;
}

/**
 * Fetch the build the channel pins for this machine, converge to it, and clear
 * the unpacked copy however that ends.
 */
export async function convergeCompanionFromChannel(
  converge: (options: CompanionInstallOptions) => Promise<CompanionInstallState>,
  options: CompanionConvergeOptions = {},
): Promise<CompanionConvergeOutcome> {
  const { from: stagedDirectory, resourceGroup, platformVersion } = options;
  let artifactManifestPath: string;
  let dispose: () => Promise<void> = () => Promise.resolve();
  if (stagedDirectory !== undefined) {
    artifactManifestPath = path.resolve(stagedDirectory, "artifact-v1.json");
  } else {
    // `{ channel: true, version }` resolves the per-version release asset, and
    // `platformTag` normalises either shape the status doc may carry ("0.6.3"
    // or "platform-v0.6.3") — a bare version would otherwise build a URL that
    // 404s against a tag-keyed asset. No custom channel host is honoured here:
    // this path has none to read, and pretending otherwise via
    // `manifestSourceForVersionTag(CHANNEL_LATEST_URL, …)` would be a
    // parameter that never varies.
    const release = await readCompanionRelease(
      platformVersion !== undefined
        ? { channel: true, version: platformTag(platformVersion) }
        : { url: CHANNEL_LATEST_URL },
    );
    if (release === null) return { state: "not-released" };
    const build = await downloadCompanionArtifact(release.component);
    artifactManifestPath = build.artifactManifestPath;
    dispose = build.dispose;
  }
  const gateway = await resolveGatewayContext({ interactive: false, resourceGroup });
  try {
    return await converge({
      ...defaultLocalCompanionInstallOptions(),
      artifactManifestPath,
      gatewayOrigin: new URL(gateway.gatewayUrl).origin,
    });
  } finally {
    await dispose();
  }
}

export async function runCompanionInstallCommand(
  stdout: WritableLike,
  install: () => Promise<CompanionConvergeOutcome>,
): Promise<number> {
  const state = await install();
  if (state.state === "not-released") {
    // This command reads the channel's `latest`. Saying "the release this
    // platform is pinned to" would describe a read it did not make — and the
    // whole point of this seam is that what is printed matches what happened.
    stdout.write(
      "The latest platform release carries no desktop companions.\n" +
      "They arrive with a platform release — run: m8t platform update\n",
    );
    return 1;
  }
  if (state.state !== "installed") {
    stdout.write("Desktop companions need repair.\nRun: m8t companion repair\n");
    return 1;
  }
  stdout.write(
    `Desktop companions installed: Stacey and Ezra are ready.\nVersion: ${state.version}\n`,
  );
  return 0;
}

export class CompanionInstallCommand extends M8tCommand {
  static paths = [["companion", "install"]];
  static usage = Command.Usage({
    description: "Install the desktop companions for this user from the release channel.",
    examples: [
      ["Install the released build", "$0 companion install"],
      [
        "Install one built from this clone",
        "$0 companion install --from apps/companion/dist-artifacts/darwin-arm64",
      ],
    ],
  });

  from = Option.String("--from", {
    description: "A locally staged build directory instead of the released one.",
  });

  resourceGroup = Option.String("--resource-group", {
    description: "Which deployment to bind to, when the subscription holds more than one.",
  });

  async executeCommand(): Promise<number> {
    return runCompanionInstallCommand(this.context.stdout, () =>
      convergeCompanionFromChannel(installCompanion, {
        ...(this.from !== undefined ? { from: this.from } : {}),
        ...(this.resourceGroup !== undefined ? { resourceGroup: this.resourceGroup } : {}),
      }),
    );
  }
}
